/**
 * Service for document translation functionality
 * Supports translating documents to multiple target languages using backend translation API
 */
import { IPublicClientApplication } from '@azure/msal-browser';
import { getAccessTokenHelper } from './msalAuth';
import { ChatState } from '../store/chatSlice';

/**
 * Returns true when `error` is a browser-level network/CORS failure.
 *
 * When a CORS preflight is rejected (or the network is unreachable) the browser
 * throws a `TypeError` *before* any HTTP response is received, so the normal
 * `response.ok` / `response.status` guards are never reached and the raw
 * message (e.g. "Failed to fetch") propagates all the way to the UI.
 *
 * Known messages by browser:
 *   Chrome / Edge – "Failed to fetch"
 *   Firefox       – "NetworkError when attempting to fetch resource"
 *   Safari        – "Load failed"
 */
function isCorsOrNetworkError(error: unknown): boolean {
  if (!(error instanceof TypeError)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('load failed')
  );
}

export interface FileTranslationConfig {
  file: Base64File; // File to translate
  target_languages: string[]; // ISO language codes (e.g., ["fr", "es"])
  glossaries?: IGlossarySpec[]; // Optional per-language glossaries
  use_default_glossary?: boolean; // Use default glossary (default: true)
}

export interface Base64File {
  filename: string; // Original filename with extension
  content_base64: string; // Base64-encoded file content
}

export interface IGlossarySpec {
  lang: string; // Target language code
  format: "TSV"; // Glossary format
  file: {
    filename: string; // Glossary filename
    content_base64: string; // Base64-encoded glossary
  };
}

/**
 * Interface for translation request payload sent to the backend API
 * 
 * This structure follows the backend API contract for document translation requests.
 * All fields are required unless specified otherwise.
 */
export interface TranslationRequest {
  // Required fields
  request_id: string; // Unique request identifier (UUID recommended)
  user_id: string; // User identifier (UPIN)
  session: string; // Session identifier to group related requests
  timestamp: string; // ISO 8601 timestamp
  query_type: "response-api"; // Always "response-api" for translation
  tools: ["translation"]; // Must include "translation"
  file_configs: FileTranslationConfig[];
}

/**
 * Interface for translation response from the backend API
 * 
 * The backend can return results in multiple nested formats, so this interface
 * includes all possible response structures to handle various API versions and states.
 */
export interface TranslationResult {
  /** Request ID echoed back from the API (optional) */
  request_id?: string;
  
  /** Session ID echoed back from the API (optional) */
  session?: string;
  
  /** Overall status of the translation operation */
  status?: 'success' | 'error' | 'processing';
  
  /** 
   * Array of successfully translated files (direct format)
   * Each file is translated to each target language, resulting in multiple output files
   */
  translated_files?: Array<{
    /** Target language code (ISO 639-1) */
    language: string;
    /** Human-readable language name (e.g., 'Spanish', 'French') */
    language_name?: string;
    /** Translated filename, often includes language suffix */
    filename: string;
    /** Translated content in base64 format */
    content_base64: string;
    /** MIME type of the translated file */
    mime_type?: string;
  }>;
  
  /** Error message if translation failed (direct format) */
  error_message?: string;
  
  /** Additional metadata about the translation operation */
  metadata?: {
    /** Detected or specified source language */
    source_language?: string;
    /** Array of target languages that were requested */
    target_languages: string[];
    /** Number of files that were translated */
    file_count: number;
    /** Timestamp when translation was completed */
    translation_timestamp: string;
  };
  
  /** 
   * Nested data structure (alternative backend response format)
   * Some API versions wrap the response in a data.content structure
   */
  data?: {
    content?: {
      /** Array of translated files in nested format */
      translated_files?: Array<{
        language: string;
        language_name?: string;
        filename: string;
        content_base64: string;
        mime_type?: string;
      }>;
      /** Error message in nested format */
      error_message?: string;
    };
  };
  
  /** 
   * Direct content structure (another alternative format)
   * Some responses have content at the root level
   */
  content?: {
    /** Array of translated files in content format */
    translated_files?: Array<{
      language: string;
      language_name?: string;
      filename: string;
      content_base64: string;
      mime_type?: string;
    }>;
    /** Error message in content format */
    error_message?: string;
  };
  
  /** 
   * Error data structure for detailed error information
   * Used when the API returns structured error responses
   */
  error_data?: {
    /** Detailed error message */
    message?: string;
  };
  
  /** Response type indicator (e.g., 'content', 'error', 'streaming') */
  type?: string;
  
  /** Completion flag for streaming responses */
  done?: any;
  
  /** Source identifier for debugging (e.g., 'translation-service') */
  source?: string;
}

/**
 * Service class for handling document translation operations
 * 
 * This service provides methods to:
 * - Translate documents to multiple languages
 * - Handle authentication via MSAL
 * - Convert files to/from base64 format
 * - Download translated files
 * 
 * @example
 * ```typescript
 * const translationService = new DocumentTranslationService(msalInstance);
 * const result = await translationService.translateDocuments(
 *   [myFile], 
 *   ['es', 'fr'], 
 *   'session-123'
 * );
 * ```
 */
export class DocumentTranslationService {
  /** Base URL for the APIM (API Management) endpoint */
  private static API_BASE_URL = process.env.REACT_APP_APIM_BASE_URL || 'https://navigator.ai.3m.com';
  
  /** MSAL instance for handling authentication */
  private msalInstance: IPublicClientApplication;

  /**
   * Creates a new DocumentTranslationService instance
   * 
   * @param msalInstance - MSAL instance for acquiring authentication tokens
   */
  constructor(msalInstance: IPublicClientApplication) {
    this.msalInstance = msalInstance;
  }

  /**
   * Get the access token for API requests (delegates to shared helper)
   */
  private async getAccessToken(): Promise<string> {
    return getAccessTokenHelper(this.msalInstance);
  }

  /**
   * Generate a unique request ID for tracking translation requests
   * 
   * Creates an ID using current timestamp and random number to ensure uniqueness
   * across multiple concurrent translation requests.
   * 
   * Format: "translation-{timestamp}-{random}"
   * Example: "translation-1704123456789-4321"
   * 
   * @returns Unique request ID string
   * 
   * @private
   */
  private generateRequestId(): string {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000);
    return `translation-${timestamp}-${random}`;
  }

  /**
   * Convert a File object to base64 string
   * 
   * Reads the file contents and encodes them in base64 format for transmission
   * to the backend API. The data URL prefix is removed to return only the base64 string.
   * 
   * @param file - File object to convert
   * @returns Promise resolving to base64-encoded string (without data URL prefix)
   * @throws Error if file reading fails
   * 
   * @private
   */
  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Translate one or more documents to one or more target languages
   * 
   * Sends files to the backend translation API and returns the translated versions.
   * Each input file will be translated to each target language, so requesting 2 files
   * with 3 languages will result in 6 translated files (2 × 3).
   * 
   * Supported file formats:
   * - Text: .txt, .html
   * - Documents: .docx, .pptx, .msg
   * - Data: .csv, .xlsx
   * 
   * @param files - Array of File objects to translate (max 2 files, 16MB each)
   * @param targetLanguages - Array of language codes (ISO 639-1 format, e.g., ['es', 'fr', 'de'])
   * @param sessionId - Session ID for tracking related requests
   * @returns Promise resolving to TranslationResult with translated files or error information
   * @throws Error if no files/languages provided or if API request fails
   * 
   * @example
   * ```typescript
   * const files = [myDocxFile, myTxtFile];
   * const result = await service.translateDocuments(
   *   files,
   *   ['es', 'fr'], // Spanish and French
   *   'session-abc-123'
   * );
   * // Result will contain 4 translated files (2 files × 2 languages)
   * ```
   */
  /**
   * Translate one or more documents to one or more target languages
   *
   * Sends files to the backend translation API and returns the translated versions.
   * Each input file will be translated to each target language, so requesting 2 files
   * with 3 languages will result in 6 translated files (2 × 3).
   *
   * @param files - Array of File objects to translate (max 2 files, 16MB each)
   * @param languages - Array of language codes (ISO 639-1 format, e.g., ['es', 'fr', 'de'])
   * @param glossaries - Optional glossary specifications to apply per-language
   * @param user - Authenticated user identifier (UPIN)
   * @param sessionId - Session ID for tracking related requests
   * @param fetchTranslationQuotaDetails - Callback invoked with quota details after request
   * @returns Promise resolving to a {@link TranslationResult} containing translated files or error information
   */
  async translateDocuments(files:File[], languages: string[], glossaries: IGlossarySpec[], user: string, sessionId:string, fetchTranslationQuotaDetails: (quota: ChatState['translationQuotaDetails']) => void
  ): Promise<TranslationResult> {
      const accessToken = await this.getAccessToken();
      const endpoint = `${DocumentTranslationService.API_BASE_URL}/nav-api/api/ai/invoke-backend`;

      const fileConfigs = await Promise.all(
        files.map(async (file) => {
          const fileBase64 = await this.fileToBase64(file);

          const fileConfig: FileTranslationConfig = {
            file: {
              filename: file.name,
              content_base64: fileBase64
            },
            target_languages: languages,
            use_default_glossary: !(glossaries && glossaries.length > 0), // Use default glossary if no glossaries provided
          };

          // Attach provided glossaries (if any) to the file config
          if (glossaries && glossaries.length > 0) {
            fileConfig.glossaries = glossaries;
          }

          return fileConfig;
        })
      );

      const requestPayload: TranslationRequest = {
        request_id: this.generateRequestId(),
        user_id: user,
        session: sessionId,
        timestamp: new Date().toISOString(),
        query_type: "response-api",
        tools: ["translation"],
        file_configs: fileConfigs
      };

      let response: Response;
      try {
        response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestPayload),
        });
      } catch (fetchError) {
        if (isCorsOrNetworkError(fetchError)) {
          throw new Error(
            'Your translation request may have been blocked by the security system. ' +
            'Please try a different file and contact support if this keeps happening.'
          );
        }
        throw fetchError;
      }

     if (!response.ok) {
        const rawText = await response.text();
        let responseBody: any = null;
        try {
          responseBody = JSON.parse(rawText);
        } catch {
          // Response body is not JSON (e.g. HTML error page from a 503)
        }
        const error = responseBody?.error;
        if (error?.code === 'quota_exceeded') {
          throw new Error(error.message);
        } else {
          const message = error?.message || responseBody?.message;
          throw new Error(`Document translation failed (${response.status})${message ? `: ${message}` : ''}`);
        }
      }

      const result = await response.json();
      
      // Check for API-level errors (type: "error" even with 200 status)
      if (result?.type === 'error') {
        const errorMessage = result?.error_data?.message || result?.error_data?.error || result?.message || 'Unknown translation error';
        throw new Error(`Translation failed: ${errorMessage}`);
      }
      
      // Check for errors in nested data structure
      if (result?.data?.error || result?.content?.error_message) {
        const errorMessage = result?.data?.error || result?.content?.error_message || 'Unknown translation error';
        throw new Error(`Translation failed: ${errorMessage}`);
      }
      this.getTranslationQuota().then(quota => {
        fetchTranslationQuotaDetails({limit: quota.quota?.quota_limit, quotaRemaining: quota.quota?.remaining, quotaResetUtc: quota.quota?.reset_time});
      });
      return result;
  }

  /**
   * Fetch translation quota for current authenticated user
   */
  /**
   * Fetch the translation quota for the current authenticated user.
   *
   * The backend returns a structured JSON object containing quota metadata
   * such as the current usage, total limit, remaining quota and reset time.
   *
   * @returns Promise resolving to an object with `success`, optional `user_id`,
   * and a `quota` object containing `quota_type`, `current_usage`, `quota_limit`,
   * `remaining`, `reset_time` (UTC ISO) and `is_exceeded` flag.
   * @throws Error when the network request fails or the response is not valid JSON
   */
  public async getTranslationQuota(): Promise<{
    success: boolean;
    user_id?: string;
    quota?: {
      quota_type: string;
      current_usage: number;
      quota_limit: number;
      remaining: number;
      reset_time: string; // UTC ISO
      is_exceeded: boolean;
    }
  }> {
    const accessToken = await this.getAccessToken();
    const endpoint = `${DocumentTranslationService.API_BASE_URL}/nav-cache-api/cache/get`;
    const resp = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        quota_type: 'translation', // quota type defined by backend for document translation,
        session_id: "tx-2001",
      })
    });
    if (!resp.ok) {
      throw new Error(`Translation quota fetch failed (${resp.status})`);
    }
    try {
      return await resp.json();
    } catch {
      throw new Error('Invalid quota JSON response');
    }
  }

  /**
   * Download a translated file from base64 content to the user's device
   * 
   * Converts the base64-encoded content back to a binary file and triggers
   * a browser download. The MIME type is automatically inferred from the
   * filename extension if not explicitly provided.
   * 
   * @param filename - Name for the downloaded file (should include extension)
   * @param contentBase64 - Base64 encoded file content
   * @param mimeType - MIME type of the file (optional, will be inferred from filename if not provided)
   * 
   * @example
   * ```typescript
   * service.downloadTranslatedFile(
   *   'document_es.docx',
   *   translatedContent,
   *   'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
   * );
   * ```
   */
  downloadTranslatedFile(filename: string, contentBase64: string, mimeType?: string): void {
    // Infer MIME type from filename extension if not provided
    const inferredMimeType = mimeType || this.getMimeTypeFromFilename(filename);

    // Convert base64 to blob
    const byteCharacters = atob(contentBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: inferredMimeType });

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Get MIME type based on file extension
   * 
   * Maps common file extensions to their corresponding MIME types.
   * Returns 'application/octet-stream' as fallback for unknown extensions.
   * 
   * @param filename - Filename with extension to analyze
   * @returns MIME type string (e.g., 'application/pdf', 'text/plain')
   * 
   * @private
   */
  private getMimeTypeFromFilename(filename: string): string {
    const extension = filename.split('.').pop()?.toLowerCase();
    const mimeTypes: { [key: string]: string } = {
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'ppt': 'application/vnd.ms-powerpoint',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'txt': 'text/plain',
      'html': 'text/html',
      'htm': 'text/html',
      'msg': 'application/vnd.ms-outlook',
    };
    return mimeTypes[extension || ''] || 'application/octet-stream';
  }
}

/**
 * Factory function to create a new DocumentTranslationService instance
 * 
 * Provides a convenient way to instantiate the service with MSAL authentication.
 * This is the recommended way to create service instances throughout the application.
 * 
 * @param msalInstance - MSAL instance for handling authentication
 * @returns New DocumentTranslationService instance
 * 
 * @example
 * ```typescript
 * import { useMsal } from '@azure/msal-react';
 * import { createDocumentTranslationService } from './documentTranslation.service';
 * 
 * function MyComponent() {
 *   const { instance } = useMsal();
 *   const translationService = createDocumentTranslationService(instance);
 *   // Use translationService...
 * }
 * ```
 */
export const createDocumentTranslationService = (msalInstance: IPublicClientApplication): DocumentTranslationService => {
  return new DocumentTranslationService(msalInstance);
};
